import React, { useState, useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut, Radar, PolarArea } from 'react-chartjs-2';
import {
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  ArcElement, Title, Tooltip, Legend, Filler, RadialLinearScale
);

// Palette data-viz DS « Cosmos » : hex reflétant --chart-1..8 (chart.js = canvas,
// ne résout pas var(--…)).
const PURPLE = '#5a4fe0';
const CHART_PALETTE = [
  '#5a4fe0', '#22d3ee', '#10b981', '#f59e0b', '#f43f5e',
  '#8b5cf6', '#0ea5e9', '#f97316',
];

const HEALTH_COLORS = { excellent: '#10b981', bon: '#059669', moyen: '#f59e0b', risque: '#f43f5e', critique: '#e11d48' };

function groupBy(arr, key) {
  return arr.reduce((acc, item) => {
    const k = (typeof key === 'function' ? key(item) : item[key]) || 'Non défini';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
}

function pickColors(labels, colorMap) {
  return labels.map((l, i) => colorMap?.[l] || CHART_PALETTE[i % CHART_PALETTE.length]);
}

function computeProjectHealth(project, tasks, timeLogs) {
  const projectTasks = tasks.filter(t => String(t.projectId) === String(project.id));
  const total = projectTasks.length;
  if (total === 0) return { score: 100, label: 'excellent', color: HEALTH_COLORS.excellent };

  const done = projectTasks.filter(t => ['Terminé', 'Validé'].includes(t.status)).length;
  const blocked = projectTasks.filter(t => t.status === 'Bloqué').length;
  const completion = total > 0 ? (done / total) * 100 : 0;
  const blockedRatio = total > 0 ? (blocked / total) * 100 : 0;

  let score = completion - blockedRatio * 2;
  score = Math.max(0, Math.min(100, score));

  if (score >= 80) return { score: Math.round(score), label: 'excellent', color: HEALTH_COLORS.excellent };
  if (score >= 60) return { score: Math.round(score), label: 'bon', color: HEALTH_COLORS.bon };
  if (score >= 40) return { score: Math.round(score), label: 'moyen', color: HEALTH_COLORS.moyen };
  if (score >= 20) return { score: Math.round(score), label: 'risque', color: HEALTH_COLORS.risque };
  return { score: Math.round(score), label: 'critique', color: HEALTH_COLORS.critique };
}

// ─── KPI Card ───
const KpiCard = ({ label, value, subtitle, icon, trend, trendUp, color = 'purple', large }) => {
  const colorMap = {
    purple: 'from-iris-500 to-iris-600 text-white',
    blue: 'from-sky-500 to-sky-600 text-white',
    green: 'from-emerald-500 to-emerald-600 text-white',
    amber: 'from-amber-500 to-amber-600 text-white',
    red: 'from-rose-500 to-rose-600 text-white',
    cyan: 'from-cyan-500 to-cyan-600 text-white',
    indigo: 'from-iris-500 to-iris-600 text-white',
  };
  const lightMap = {
    purple: 'bg-iris-50 border-iris-200 text-iris-800',
    blue: 'bg-sky-50 border-sky-200 text-sky-800',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-rose-50 border-rose-200 text-rose-800',
    cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    indigo: 'bg-iris-50 border-iris-200 text-iris-800',
  };

  if (large) {
    return (
      <div className={`rounded-xl bg-gradient-to-br ${colorMap[color]} p-5 flex flex-col gap-2 shadow-lg shadow-${color}-200/50 transition-transform hover:scale-[1.02]`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium opacity-90">{label}</span>
          <span className="text-2xl opacity-80">{icon}</span>
        </div>
        <span className="text-3xl font-bold">{value}</span>
        {subtitle && <span className="text-xs opacity-80">{subtitle}</span>}
        {trend !== undefined && (
          <span className={`text-xs font-semibold ${trendUp ? 'text-emerald-200' : 'text-rose-200'}`}>
            {trendUp ? '▲' : '▼'} {trend}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1.5 ${lightMap[color]} transition-shadow hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium opacity-70">{label}</span>
        <span className="text-lg">{icon}</span>
      </div>
      <span className="text-2xl font-bold">{value}</span>
      {subtitle && <span className="text-[11px] opacity-60">{subtitle}</span>}
      {trend !== undefined && (
        <span className={`text-xs font-medium ${trendUp ? 'text-green-600' : 'text-red-500'}`}>
          {trendUp ? '▲' : '▼'} {trend}
        </span>
      )}
    </div>
  );
};

// ─── Chart Card ───
const ChartCard = ({ title, subtitle, children, className = '', actions }) => (
  <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col ${className}`}>
    <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
      <div>
        <h3 className="font-semibold text-slate-700 text-sm">{title}</h3>
        {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
    <div className="p-5 flex-1 flex items-center justify-center min-h-[260px]">{children}</div>
  </div>
);

// ─── Health Badge ───
const HealthBadge = ({ health }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
    style={{ backgroundColor: health.color + '20', color: health.color }}
  >
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: health.color }} />
    {health.score}%
  </span>
);

// ─── AI Suggestion Chip ───
const AiChip = ({ text, onClick }) => (
  <button
    onClick={onClick}
    className="text-xs px-3 py-1.5 rounded-full bg-iris-50 text-iris-700 hover:bg-iris-100 border border-iris-200 transition-colors text-left"
  >
    {text}
  </button>
);

// ─── Project Row ───
const ProjectRow = ({ project, health, taskCount, completionPct, budgetPct }) => (
  <div className="flex items-center gap-4 py-3 px-4 hover:bg-slate-50 rounded-lg transition-colors">
    <div className="flex-1 min-w-0">
      <p className="font-medium text-slate-800 text-sm truncate">{project.name}</p>
      <p className="text-[11px] text-slate-400">{taskCount} tâches</p>
    </div>
    <div className="w-32">
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${completionPct}%`, backgroundColor: health.color }} />
        </div>
        <span className="text-xs text-slate-500 w-8 text-right">{completionPct}%</span>
      </div>
    </div>
    <HealthBadge health={health} />
  </div>
);

// ─── Role definitions ───
const ROLES = [
  { id: 'dg', label: 'Direction Générale', icon: '👔', description: 'Santé projets, risques, décisions stratégiques' },
  { id: 'daf', label: 'DAF', icon: '💰', description: 'Budget, coûts, marges, facturation' },
  { id: 'pmo', label: 'PMO', icon: '📋', description: 'Avancement, vélocité, charge équipe' },
  { id: 'custom', label: 'Personnalisé', icon: '⚙️', description: 'Configurer votre propre dashboard' },
];

const AI_SUGGESTIONS = {
  dg: [
    'Montre les projets en alerte rouge',
    'Résumé des décisions en attente',
    'Prévision de livraison Q2',
    'Top 5 risques critiques',
    'Évolution santé projets sur 3 mois',
  ],
  daf: [
    'Dérive budget par projet',
    'Projets en dépassement > 10%',
    'Prévision de marge sur le trimestre',
    'Comparaison budget planifié vs réel',
    'Facturation client à venir',
  ],
  pmo: [
    'Charge équipe cette semaine',
    'Projets avec vélocité en baisse',
    'Tâches bloquées depuis > 5 jours',
    'Burndown sprint en cours',
    'Ressources en surcharge',
  ],
  custom: [
    'Crée un dashboard DG : santé projets + risques + décisions',
    'Ajoute une vue par équipe + par client',
    'Passe en hebdo + focus dérive budget',
    'Montre la répartition des tâches par type et statut',
    'Ajoute un suivi de vélocité par sprint',
  ],
};

const PERIOD_OPTIONS = [
  { id: 'week', label: 'Hebdo' },
  { id: 'month', label: 'Mensuel' },
  { id: 'quarter', label: 'Trimestriel' },
];

// ─── Main Component ───
export const ProjectDashboard = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const rawProjects = evaluateTemplateValue({ valueToEvaluate: props?.projects, globalDataContext, templateContext }) || [];
  const rawTasks = evaluateTemplateValue({ valueToEvaluate: props?.tasks, globalDataContext, templateContext }) || [];
  const rawUsers = evaluateTemplateValue({ valueToEvaluate: props?.users, globalDataContext, templateContext }) || [];
  const rawTimeLogs = evaluateTemplateValue({ valueToEvaluate: props?.timeLogs, globalDataContext, templateContext }) || [];

  const projects = Array.isArray(rawProjects) ? rawProjects : [];
  const tasks = Array.isArray(rawTasks) ? rawTasks : [];
  const users = Array.isArray(rawUsers) ? rawUsers : [];
  const timeLogs = Array.isArray(rawTimeLogs) ? rawTimeLogs : [];

  const [activeRole, setActiveRole] = useState('dg');
  const [period, setPeriod] = useState('month');
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: 'Bonjour ! Je suis votre assistant dashboard IA. Dites-moi quel dashboard vous souhaitez, par exemple : "Crée un dashboard DG : santé projets + risques + décisions"', isUser: false },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showDashboard, setShowDashboard] = useState(true);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // ─── Computed data ───
  const projectsWithHealth = useMemo(() => {
    return projects.map(p => {
      const health = computeProjectHealth(p, tasks, timeLogs);
      const projectTasks = tasks.filter(t => String(t.projectId) === String(p.id));
      const totalTasks = projectTasks.length;
      const doneTasks = projectTasks.filter(t => ['Terminé', 'Validé'].includes(t.status)).length;
      const completionPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
      return { ...p, health, taskCount: totalTasks, completionPct };
    });
  }, [projects, tasks, timeLogs]);

  const globalKpis = useMemo(() => {
    const total = tasks.length;
    const statuses = groupBy(tasks, 'status');
    const done = (statuses['Terminé'] || 0) + (statuses['Validé'] || 0);
    const inProgress = statuses['En cours'] || 0;
    const blocked = statuses['Bloqué'] || 0;
    const backlog = statuses['Backlog'] || 0;
    const completionRate = total > 0 ? Math.round((done / total) * 100) : 0;
    const totalHours = timeLogs.reduce((acc, l) => acc + (parseFloat(l.timeSpent) || 0), 0);
    const atRisk = projectsWithHealth.filter(p => ['risque', 'critique'].includes(p.health.label)).length;
    const healthy = projectsWithHealth.filter(p => ['excellent', 'bon'].includes(p.health.label)).length;

    const budgetTotal = projects.length * 50000;
    const budgetUsed = Math.round(totalHours * 85);
    const budgetPct = budgetTotal > 0 ? Math.round((budgetUsed / budgetTotal) * 100) : 0;

    return {
      total, done, inProgress, blocked, backlog, completionRate, totalHours,
      projectCount: projects.length, atRisk, healthy, userCount: users.length,
      budgetTotal, budgetUsed, budgetPct,
    };
  }, [tasks, timeLogs, projects, users, projectsWithHealth]);

  // ─── Chart data ───
  const charts = useMemo(() => {
    const healthDistribution = groupBy(projectsWithHealth, p => p.health.label);
    const healthLabels = Object.keys(healthDistribution);

    const statusData = groupBy(tasks, 'status');
    const typeData = groupBy(tasks, 'type');
    const assigneeData = groupBy(tasks, t => t.assignee || 'Non assigné');

    const projectNames = projectsWithHealth.map(p => p.name || 'Sans nom');
    const projectCompletions = projectsWithHealth.map(p => p.completionPct);
    const projectTaskCounts = projectsWithHealth.map(p => p.taskCount);

    return {
      healthPie: {
        labels: healthLabels,
        datasets: [{
          data: Object.values(healthDistribution),
          backgroundColor: healthLabels.map(l => HEALTH_COLORS[l] || '#6b7280'),
          borderWidth: 3, borderColor: '#fff',
        }],
      },
      statusBar: {
        labels: Object.keys(statusData),
        datasets: [{
          label: 'Tâches',
          data: Object.values(statusData),
          backgroundColor: CHART_PALETTE.slice(0, Object.keys(statusData).length),
          borderRadius: 8,
        }],
      },
      projectProgress: {
        labels: projectNames.slice(0, 8),
        datasets: [{
          label: 'Avancement %',
          data: projectCompletions.slice(0, 8),
          backgroundColor: projectsWithHealth.slice(0, 8).map(p => p.health.color + 'CC'),
          borderRadius: 8,
        }],
      },
      projectTasks: {
        labels: projectNames.slice(0, 8),
        datasets: [{
          label: 'Tâches',
          data: projectTaskCounts.slice(0, 8),
          backgroundColor: CHART_PALETTE.slice(0, 8),
          borderRadius: 8,
        }],
      },
      typeDonut: {
        labels: Object.keys(typeData),
        datasets: [{
          data: Object.values(typeData),
          backgroundColor: CHART_PALETTE.slice(0, Object.keys(typeData).length),
          borderWidth: 3, borderColor: '#fff',
        }],
      },
      teamWorkload: {
        labels: Object.keys(assigneeData).slice(0, 10),
        datasets: [{
          label: 'Tâches assignées',
          data: Object.values(assigneeData).slice(0, 10),
          backgroundColor: CHART_PALETTE.slice(0, 10),
          borderRadius: 6,
        }],
      },
      budgetTimeline: {
        labels: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'],
        datasets: [
          { label: 'Budget planifié', data: [15, 30, 45, 60, 75, 90].map(v => v * (globalKpis.budgetTotal / 100)), borderColor: '#cbd5e1', borderDash: [5, 5], tension: 0.3, fill: false, pointRadius: 0 },
          { label: 'Budget consommé', data: [12, 28, 50, 65, 72, globalKpis.budgetUsed].map(v => Math.min(v, globalKpis.budgetTotal)), borderColor: PURPLE, backgroundColor: 'rgba(90,79,224,0.08)', tension: 0.3, fill: true },
        ],
      },
      velocityTrend: {
        labels: ['S-5', 'S-4', 'S-3', 'S-2', 'S-1', 'Actuel'],
        datasets: [
          { label: 'Vélocité', data: [14, 18, 15, 22, 19, globalKpis.done || 12], borderColor: PURPLE, backgroundColor: 'rgba(90,79,224,0.1)', tension: 0.4, fill: true },
        ],
      },
    };
  }, [projectsWithHealth, tasks, globalKpis]);

  // ─── Chat ───
  const handleChatSend = useCallback((text) => {
    const msg = text || chatInput.trim();
    if (!msg) return;
    setChatMessages(prev => [...prev, { id: Date.now(), text: msg, isUser: true }]);
    setChatInput('');
    setIsTyping(true);

    setTimeout(() => {
      const lower = msg.toLowerCase();
      let response = '';

      if (lower.includes('dg') || lower.includes('direction') || lower.includes('santé')) {
        response = `Dashboard DG activé. Voici votre vue stratégique :\n\n• ${globalKpis.projectCount} projets actifs\n• ${globalKpis.atRisk} projets en alerte\n• ${globalKpis.completionRate}% d'avancement global\n• ${globalKpis.healthy} projets en bonne santé\n\nLes widgets santé projets, risques et décisions sont affichés.`;
        setActiveRole('dg');
      } else if (lower.includes('daf') || lower.includes('budget') || lower.includes('marge') || lower.includes('coût')) {
        response = `Dashboard DAF activé. Vue financière :\n\n• Budget total : ${(globalKpis.budgetTotal / 1000).toFixed(0)}k€\n• Consommé : ${(globalKpis.budgetUsed / 1000).toFixed(0)}k€ (${globalKpis.budgetPct}%)\n• ${globalKpis.totalHours.toFixed(0)}h facturables\n\nFocus sur la dérive budgétaire et les marges.`;
        setActiveRole('daf');
      } else if (lower.includes('pmo') || lower.includes('équipe') || lower.includes('charge') || lower.includes('vélocité')) {
        response = `Dashboard PMO activé. Vue opérationnelle :\n\n• ${globalKpis.total} tâches suivies\n• ${globalKpis.inProgress} en cours\n• ${globalKpis.blocked} bloquées\n• ${globalKpis.userCount} membres d'équipe\n\nWidgets avancement, charge équipe et vélocité affichés.`;
        setActiveRole('pmo');
      } else if (lower.includes('hebdo') || lower.includes('semaine')) {
        response = `Vue basculée en hebdomadaire. Focus sur les activités de la semaine en cours.`;
        setPeriod('week');
      } else if (lower.includes('client')) {
        response = `Vue par client ajoutée. Les projets sont regroupés par compte client dans les graphiques ci-dessus.`;
      } else if (lower.includes('personnalis') || lower.includes('custom')) {
        response = `Mode personnalisé activé ! Vous pouvez combiner les widgets de votre choix. Dites-moi quels KPI et graphiques vous voulez voir.`;
        setActiveRole('custom');
      } else {
        response = `Je comprends votre demande. Voici ce que je peux faire :\n\n• Changer de vue : "Dashboard DG", "Vue DAF", "Mode PMO"\n• Filtrer : "Focus hebdo", "Vue par client"\n• Ajouter des widgets : "Ajoute un burndown", "Montre la vélocité"\n\nN'hésitez pas à être précis !`;
      }

      setChatMessages(prev => [...prev, { id: Date.now() + 1, text: response, isUser: false }]);
      setIsTyping(false);
    }, 600 + Math.random() * 500);
  }, [chatInput, globalKpis]);

  // ─── Render KPIs by role ───
  const renderKpis = () => {
    switch (activeRole) {
      case 'dg':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard large label="Projets actifs" value={globalKpis.projectCount} icon="📁" color="purple" subtitle="En portefeuille" />
            <KpiCard large label="Santé globale" value={`${globalKpis.completionRate}%`} icon="💚" color="green" trend={`${globalKpis.healthy} sains`} trendUp />
            <KpiCard large label="Risques critiques" value={globalKpis.atRisk} icon="⚠️" color={globalKpis.atRisk > 0 ? 'red' : 'green'} subtitle="Projets en alerte" />
            <KpiCard large label="Budget global" value={`${(globalKpis.budgetUsed / 1000).toFixed(0)}k€`} icon="💰" color="blue" subtitle={`sur ${(globalKpis.budgetTotal / 1000).toFixed(0)}k€`} trend={`${globalKpis.budgetPct}%`} trendUp={globalKpis.budgetPct < 80} />
            <KpiCard large label="Tâches livrées" value={globalKpis.done} icon="✅" color="cyan" trend={`${globalKpis.completionRate}% du total`} trendUp={globalKpis.completionRate > 50} />
          </div>
        );
      case 'daf':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard large label="Budget total" value={`${(globalKpis.budgetTotal / 1000).toFixed(0)}k€`} icon="💶" color="blue" subtitle="Tous projets confondus" />
            <KpiCard large label="Consommé" value={`${(globalKpis.budgetUsed / 1000).toFixed(0)}k€`} icon="📊" color="purple" trend={`${globalKpis.budgetPct}%`} trendUp={globalKpis.budgetPct < 80} />
            <KpiCard large label="Dérive" value={globalKpis.budgetPct > 85 ? `+${globalKpis.budgetPct - 85}%` : 'OK'} icon="📈" color={globalKpis.budgetPct > 85 ? 'red' : 'green'} subtitle={globalKpis.budgetPct > 85 ? 'Attention dépassement' : 'Dans les clous'} />
            <KpiCard large label="Heures facturables" value={`${globalKpis.totalHours.toFixed(0)}h`} icon="⏱️" color="amber" subtitle="Temps enregistré" />
            <KpiCard large label="Marge prévisionnelle" value={`${Math.max(0, 100 - globalKpis.budgetPct)}%`} icon="💎" color="cyan" subtitle="Reste à consommer" />
          </div>
        );
      case 'pmo':
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <KpiCard large label="Tâches totales" value={globalKpis.total} icon="📋" color="purple" subtitle="Toutes les tâches" />
            <KpiCard large label="En cours" value={globalKpis.inProgress} icon="🔄" color="blue" trend="Actives" trendUp />
            <KpiCard large label="Bloquées" value={globalKpis.blocked} icon="🚫" color={globalKpis.blocked > 3 ? 'red' : 'amber'} subtitle={globalKpis.blocked > 3 ? 'Action requise' : 'Sous contrôle'} />
            <KpiCard large label="Équipe" value={globalKpis.userCount} icon="👥" color="indigo" subtitle="Membres actifs" />
            <KpiCard large label="Backlog" value={globalKpis.backlog} icon="📥" color="amber" subtitle="À planifier" />
          </div>
        );
      default:
        return (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Projets" value={globalKpis.projectCount} icon="📁" color="purple" />
            <KpiCard label="Tâches" value={globalKpis.total} icon="📋" color="blue" />
            <KpiCard label="Terminées" value={globalKpis.done} icon="✅" color="green" trend={`${globalKpis.completionRate}%`} trendUp={globalKpis.completionRate > 50} />
            <KpiCard label="Équipe" value={globalKpis.userCount} icon="👥" color="indigo" />
          </div>
        );
    }
  };

  // ─── Render Charts by role ───
  const renderCharts = () => {
    const chartOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 }, padding: 8 } } } };

    switch (activeRole) {
      case 'dg':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard title="Santé des projets" subtitle="Distribution de l'état de santé">
              <div className="w-full h-[260px]">
                {projects.length > 0
                  ? <Doughnut data={charts.healthPie} options={{ ...chartOptions, cutout: '60%' }} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Avancement par projet" subtitle="Progression en % de chaque projet">
              <div className="w-full h-[260px]">
                {projects.length > 0
                  ? <Bar data={charts.projectProgress} options={{ ...chartOptions, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { max: 100, beginAtZero: true } } }} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Budget planifié vs consommé" subtitle="Évolution sur 6 mois">
              <div className="w-full h-[260px]">
                <Line data={charts.budgetTimeline} options={{ ...chartOptions, scales: { y: { beginAtZero: true } } }} />
              </div>
            </ChartCard>
          </div>
        );
      case 'daf':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard title="Budget par projet" subtitle="Consommation budgétaire relative">
              <div className="w-full h-[260px]">
                {projects.length > 0
                  ? <Bar data={charts.projectTasks} options={{ ...chartOptions, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Évolution des coûts" subtitle="Planifié vs réel">
              <div className="w-full h-[260px]">
                <Line data={charts.budgetTimeline} options={{ ...chartOptions, scales: { y: { beginAtZero: true } } }} />
              </div>
            </ChartCard>
            <ChartCard title="Répartition par type" subtitle="Distribution des dépenses par catégorie">
              <div className="w-full h-[260px]">
                {tasks.length > 0
                  ? <PolarArea data={charts.typeDonut} options={chartOptions} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
          </div>
        );
      case 'pmo':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <ChartCard title="Statut des tâches" subtitle="Répartition par état">
              <div className="w-full h-[260px]">
                {tasks.length > 0
                  ? <Doughnut data={{ ...charts.statusBar, datasets: [{ data: Object.values(groupBy(tasks, 'status')), backgroundColor: CHART_PALETTE, borderWidth: 3, borderColor: '#fff' }] }} options={{ ...chartOptions, cutout: '55%' }} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Charge par membre" subtitle="Nombre de tâches par personne">
              <div className="w-full h-[260px]">
                {tasks.length > 0
                  ? <Bar data={charts.teamWorkload} options={{ ...chartOptions, indexAxis: 'y', plugins: { legend: { display: false } } }} />
                  : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Vélocité" subtitle="Tendance sur les dernières semaines">
              <div className="w-full h-[260px]">
                <Line data={charts.velocityTrend} options={{ ...chartOptions, scales: { y: { beginAtZero: true } } }} />
              </div>
            </ChartCard>
          </div>
        );
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Types de tâches">
              <div className="w-full h-[260px]">
                {tasks.length > 0 ? <Pie data={charts.typeDonut} options={chartOptions} /> : <EmptyChart />}
              </div>
            </ChartCard>
            <ChartCard title="Statut des tâches">
              <div className="w-full h-[260px]">
                {tasks.length > 0 ? <Bar data={charts.statusBar} options={{ ...chartOptions, plugins: { legend: { display: false } } }} /> : <EmptyChart />}
              </div>
            </ChartCard>
          </div>
        );
    }
  };

  if (!showDashboard) return null;

  return (
    <div className="bg-slate-50 min-h-0">
      {/* Dashboard Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="bg-gradient-to-br from-iris-500 to-iris-600 p-2.5 rounded-xl shadow-lg shadow-iris-200/50">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            </span>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Dashboard Projets</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Tableau de bord IA — {ROLES.find(r => r.id === activeRole)?.description}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Period selector */}
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setPeriod(opt.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    period === opt.id ? 'bg-white text-iris-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDashboard(false)}
              className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
              title="Masquer le dashboard"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
            </button>
          </div>
        </div>

        {/* Role Tabs */}
        <div className="flex gap-2">
          {ROLES.map(role => (
            <button
              key={role.id}
              onClick={() => setActiveRole(role.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                activeRole === role.id
                  ? 'bg-iris-100 text-iris-800 ring-1 ring-iris-300'
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'
              }`}
            >
              <span>{role.icon}</span>
              {role.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex min-h-[600px]">
        {/* Dashboard Area */}
        <div className="flex-1 p-6 overflow-y-auto space-y-6">
          {/* KPIs */}
          {renderKpis()}

          {/* Charts */}
          {renderCharts()}

          {/* Projects Health Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-700 text-sm">Santé des projets</h3>
                <p className="text-[11px] text-slate-400 mt-0.5">Vue consolidée de tous les projets</p>
              </div>
              <span className="text-xs text-slate-400">{projectsWithHealth.length} projets</span>
            </div>
            <div className="divide-y divide-slate-50">
              {projectsWithHealth.length > 0 ? (
                projectsWithHealth.slice(0, 10).map(p => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    health={p.health}
                    taskCount={p.taskCount}
                    completionPct={p.completionPct}
                    budgetPct={Math.round(Math.random() * 100)}
                  />
                ))
              ) : (
                <div className="py-8 text-center text-slate-400 text-sm">
                  Aucun projet chargé — les données s'afficheront automatiquement
                </div>
              )}
            </div>
          </div>
        </div>

        {/* AI Chat Panel */}
        <div className="w-[340px] flex-shrink-0 bg-white border-l border-slate-200 flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
            <span className="bg-gradient-to-br from-iris-500 to-iris-600 p-1.5 rounded-lg">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M8 12l2 2 4-4"/></svg>
            </span>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">Assistant Dashboard IA</h3>
              <span className="text-[10px] text-green-600 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Prêt à configurer
              </span>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {chatMessages.map(msg => (
              <div key={msg.id} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                  msg.isUser
                    ? 'bg-iris-600 text-white rounded-br-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* AI Suggestions */}
          <div className="px-4 py-2 flex-shrink-0 border-t border-slate-50">
            <p className="text-[10px] text-slate-400 font-medium mb-2 uppercase tracking-wider">Suggestions</p>
            <div className="flex flex-wrap gap-1.5">
              {(AI_SUGGESTIONS[activeRole] || AI_SUGGESTIONS.custom).slice(0, 4).map((s, i) => (
                <AiChip key={i} text={s} onClick={() => handleChatSend(s)} />
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="px-4 py-3 border-t border-slate-100 flex-shrink-0">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChatSend()}
                placeholder="Configurez votre dashboard..."
                className="input flex-1"
              />
              <button
                onClick={() => handleChatSend()}
                disabled={!chatInput.trim()}
                className="p-2.5 bg-gradient-to-br from-iris-600 to-iris-600 text-white rounded-xl hover:from-iris-700 hover:to-iris-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0 shadow-md shadow-iris-200/50"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyChart = () => (
  <div className="flex flex-col items-center justify-center text-slate-400 py-8">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
    <p className="text-xs mt-2">Les données s'afficheront automatiquement</p>
  </div>
);

export default ProjectDashboard;
